import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const VARGAS = ["D1", "D2", "D3", "D7", "D9", "D10", "D12", "D16", "D20",
                 "D24", "D27", "D30", "D40", "D45", "D60"];
const VARGA_MEANING = {
  D1: "Rasi · Overall body & life",
  D2: "Hora · Wealth",
  D3: "Drekkana · Siblings, courage",
  D7: "Saptamsa · Children",
  D9: "Navamsa · Marriage & dharma",
  D10: "Dasamsa · Career & status",
  D12: "Dwadasamsa · Parents",
  D16: "Shodasamsa · Vehicles & comforts",
  D20: "Vimsamsa · Spirituality",
  D24: "Chaturvimsamsa · Education",
  D27: "Nakshatramsa · Strengths & weaknesses",
  D30: "Trimsamsa · Misfortunes",
  D40: "Khavedamsa · Auspicious effects",
  D45: "Akshavedamsa · General life",
  D60: "Shashtiamsa · Deepest karma",
};

function md(text) {
  if (!text) return null;
  return text.split("\n").map((l, i) => {
    const k = `L${i}-${l.slice(0, 24)}`;
    if (l.startsWith("## ")) return <h3 key={k} className="font-heading text-lg text-[#D4AF37] mt-4 mb-2">{l.slice(3)}</h3>;
    if (l.startsWith("# ")) return <h2 key={k} className="font-heading text-xl text-[#E6E1D6] mt-4 mb-2">{l.slice(2)}</h2>;
    if (!l.trim()) return <div key={k} className="h-2" />;
    return <p key={k} className="text-sm text-[#E6E1D6] leading-relaxed font-light">{l}</p>;
  });
}

export default function YogasPanel({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [varga, setVarga] = useState("D1");
  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.yogas(profileId, varga)); }
    catch (e) {
      console.error("yogas failed:", e);
      toast.error(e?.response?.data?.detail || "Failed");
    }
    setLoading(false);
  }, [profileId, varga]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return (
    <div className="space-y-4" data-testid="yogas-panel">
      <VargaSelect varga={varga} onChange={setVarga} />
      <div className="text-[#A19E95] font-mono text-sm">Scanning for yogas in {varga}… (~60s first time)</div>
    </div>
  );
  if (!data) return null;
  const present = data.yogas.filter(y => y.present);
  const absent = data.yogas.filter(y => !y.present);

  return (
    <div className="space-y-6" data-testid="yogas-panel">
      <VargaSelect varga={varga} onChange={setVarga} loading={loading} />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="border border-[#2A2D35] bg-[#181A1F] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Checked</div>
          <div className="font-heading text-3xl text-[#E6E1D6]">{data.summary.total_checked}</div>
        </div>
        <div className="border border-[#7B906F]/60 bg-[#181A1F] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#7B906F] font-mono">Present</div>
          <div className="font-heading text-3xl text-[#7B906F]">{data.summary.present_count}</div>
        </div>
        <div className="border border-[#2A2D35] bg-[#181A1F] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#6E6B64] font-mono">Absent</div>
          <div className="font-heading text-3xl text-[#6E6B64]">{data.summary.absent_count}</div>
        </div>
        <div className="border border-[#D4AF37]/40 bg-[#181A1F] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Categories Active</div>
          <div className="font-heading text-3xl text-[#D4AF37]">{Object.keys(data.summary.by_category).length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#7B906F] font-mono mb-3">Present ({present.length})</h3>
          <div className="space-y-2">
            {present.map(y => (
              <div key={y.name} data-testid={`yoga-${y.name.replace(/[^a-zA-Z]/g,'_')}`}
                   className="border border-[#7B906F]/40 bg-[#181A1F] p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-[#7B906F] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="font-heading text-base text-[#E6E1D6]">{y.name}</div>
                      <div className="text-[10px] font-mono text-[#A8896C] uppercase tracking-widest">{y.category}</div>
                    </div>
                    <div className="text-xs text-[#A19E95] font-mono mt-1">{y.detail}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#6E6B64] font-mono mb-3">Absent ({absent.length})</h3>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {absent.map(y => (
              <div key={y.name} className="flex items-start gap-2 text-xs py-1">
                <XCircle size={12} className="text-[#6E6B64] shrink-0 mt-0.5" />
                <span className="text-[#6E6B64]">{y.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {data.narrative && (
        <div className="border border-[#2A2D35] bg-[#181A1F] p-6" data-testid="yogas-narrative">
          {md(data.narrative)}
        </div>
      )}
    </div>
  );
}

function VargaSelect({ varga, onChange, loading }) {
  return (
    <div className="flex flex-wrap items-end gap-3 border border-[#2A2D35] bg-[#181A1F] p-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono mb-2">
          Check Yogas In · Divisional Chart
        </div>
        <Select value={varga} onValueChange={onChange} disabled={loading}>
          <SelectTrigger data-testid="yogas-varga-select"
                         className="w-72 bg-[#0F1115] border-[#2A2D35] text-[#E6E1D6]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#181A1F] border-[#2A2D35] text-[#E6E1D6]">
            {VARGAS.map(v => (
              <SelectItem key={v} value={v}>
                <span className="font-mono">{v}</span>
                <span className="text-[#6E6B64] ml-2 text-xs">— {VARGA_MEANING[v]}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="text-[11px] text-[#A19E95] font-mono flex-1 max-w-xl">
        <span className="text-[#A8896C]">Currently scanning:</span> {VARGA_MEANING[varga]}
        <div className="text-[10px] text-[#6E6B64] mt-1">
          Yogas in D9 refine marriage matters; D10 for career; D60 reveals deepest karmic patterns.
        </div>
      </div>
    </div>
  );
}

